import React,{useState}from"react";
import{AlertCircle,ChevronRight,ChevronLeft,ChevronDown,ChevronUp,MessageSquare,Shield,Inbox,Bell,ThumbsUp,ThumbsDown,UserCheck,Users,GripVertical,Pin,Zap,Layers,Radio,CheckCircle2,Calendar,Clock,TrendingUp,TrendingDown,Target,Activity,List}from"lucide-react";
import{BLUE,GOLD,PRI_ORDER,STA_ORDER,fmtDate,isOverdue,dispName,needsStandupUpdate}from"../config";
import{StaBadge,PriBadge,AccBadge,VisIcon}from"./UI";
import{TaskRow}from"./TaskCard";

// Tasks completed more than 15 days ago are hidden from completed lists (still in SP)
const COMPLETED_DAYS=15;
function completedCutoff(){return new Date(Date.now()-COMPLETED_DAYS*86400000).toISOString().slice(0,10);}
function withinCompletedWindow(t){
  if(!t.completedDate)return true; // no completedDate recorded (old tasks) — show them
  return t.completedDate>=completedCutoff();
}

//  Dashboard
export function Dashboard({allTasks,myTasks,setSel,setView,mode,cu,allUsers,onAccept,onDecline,onApprove,onReopen}){
  const email=cu?.email?.toLowerCase()||"";
  const today=new Date().toISOString().slice(0,10);
  const tomorrow=new Date(Date.now()+86400000).toISOString().slice(0,10);
  const thisWeek=new Date(Date.now()+6*86400000).toISOString().slice(0,10);
  const isAdminRole=cu?.role==="Admin";
  const isManagerRole=cu?.role==="Manager"||cu?.role==="Admin";

  // Action list data
  const dateActive=myTasks.filter(t=>!["completed","declined"].includes(t.sta)&&(t.acc!=="pending"||t.own.toLowerCase()===email));
  const pastDue=dateActive.filter(t=>t.dueDate&&t.dueDate<today).sort((a,b)=>a.dueDate.localeCompare(b.dueDate));
  const dueToday=dateActive.filter(t=>t.dueDate===today).sort((a,b)=>({high:0,medium:1,low:2}[a.pri]??1)-({high:0,medium:1,low:2}[b.pri]??1));
  const dueTomorrow=dateActive.filter(t=>t.dueDate===tomorrow).sort((a,b)=>({high:0,medium:1,low:2}[a.pri]??1)-({high:0,medium:1,low:2}[b.pri]??1));
  const needsApproval=cu?(
    isAdminRole
      ?allTasks.filter(t=>t.sta==="review")
      :isManagerRole
        ?allTasks.filter(t=>t.sta==="review"&&(
            t.own.toLowerCase()===email||
            (t.listType==="company"||t.vis==="company")||
            (()=>{const ar=(allUsers||[]).find(u=>u.email.toLowerCase()===t.asgn?.toLowerCase());return ar&&(ar.manager1?.toLowerCase()===email||ar.manager2?.toLowerCase()===email);})()
          ))
        :myTasks.filter(t=>t.sta==="review"&&t.own.toLowerCase()===email)
  ):[];
  const assignedToMe=cu?myTasks.filter(t=>
    t.asgn.toLowerCase()===email&&t.own.toLowerCase()!==email&&
    !["completed","declined"].includes(t.sta)
  ).sort((a,b)=>({pending:0,accepted:1,"":2}[a.acc]??2)-({pending:0,accepted:1,"":2}[b.acc]??2)):[];
  const assignedByMe=cu?allTasks.filter(t=>{
    if(!t.asgn||t.asgn.toLowerCase()===email)return false;
    if(t.sta==="completed")return false;
    // Show declined only if assignee declined (acc="declined") — not if owner/admin declined the task
    if(t.sta==="declined"&&t.acc!=="declined")return false;
    const owns=t.own.toLowerCase()===email;
    const reqByMe=!!(t.reqBy&&t.reqBy.toLowerCase()===email);
    return owns||reqByMe;
  }).sort((a,b)=>({declined:0,pending:1,review:2,accepted:3,"":4}[a.sta==="review"?"review":(a.acc||"")]??4)-({declined:0,pending:1,review:2,accepted:3,"":4}[b.sta==="review"?"review":(b.acc||"")]??4)):[];

  // Stats toggle
  const[statsView,setStatsView]=useState("mine");

  // ── MY TASKS stats ──
  const myActive=myTasks.filter(t=>!["completed","declined","pending"].includes(t.sta));
  const myCompleted=myTasks.filter(t=>t.sta==="completed");
  const myOverdue=myActive.filter(t=>t.dueDate&&t.dueDate<today);
  const myBlocked=myActive.filter(t=>t.sta==="blocked");
  const myTotal=myActive.length+myCompleted.length;
  const myCompRate=myTotal>0?Math.round(myCompleted.length/myTotal*100):0;
  const myActiveWithDue=myActive.filter(t=>t.dueDate);
  const myOverdueRate=myActiveWithDue.length>0?Math.round(myOverdue.length/myActiveWithDue.length*100):0;
  const myDueThisWeek=myActive.filter(t=>t.dueDate&&t.dueDate>=today&&t.dueDate<=thisWeek);
  const mySta={todo:0,in_progress:0,blocked:0,review:0,completed:0};
  myTasks.filter(t=>!["declined","pending"].includes(t.sta)).forEach(t=>{if(mySta[t.sta]!==undefined)mySta[t.sta]++;});
  const myPri={high:0,medium:0,low:0};
  myActive.forEach(t=>{if(myPri[t.pri]!==undefined)myPri[t.pri]++;});

  // ── COMPANY stats ──
  const cmpActive=allTasks.filter(t=>!["completed","declined","pending"].includes(t.sta));
  const cmpCompleted=allTasks.filter(t=>t.sta==="completed");
  const cmpOverdue=cmpActive.filter(t=>t.dueDate&&t.dueDate<today);
  const cmpBlocked=cmpActive.filter(t=>t.sta==="blocked");
  const cmpTotal=cmpActive.length+cmpCompleted.length;
  const cmpCompRate=cmpTotal>0?Math.round(cmpCompleted.length/cmpTotal*100):0;
  const cmpActiveWithDue=cmpActive.filter(t=>t.dueDate);
  const cmpOverdueRate=cmpActiveWithDue.length>0?Math.round(cmpOverdue.length/cmpActiveWithDue.length*100):0;
  const cmpPeople=new Set([...cmpActive.map(t=>t.own.toLowerCase()),...cmpActive.filter(t=>t.asgn&&t.acc==="accepted").map(t=>t.asgn.toLowerCase())]);
  const cmpSta={todo:0,in_progress:0,blocked:0,review:0,completed:0};
  allTasks.filter(t=>!["declined","pending"].includes(t.sta)).forEach(t=>{if(cmpSta[t.sta]!==undefined)cmpSta[t.sta]++;});
  const cmpPri={high:0,medium:0,low:0};
  cmpActive.forEach(t=>{if(cmpPri[t.pri]!==undefined)cmpPri[t.pri]++;});

  // Which set to show
  const isMine=statsView==="mine";
  const sActive=isMine?myActive:cmpActive;
  const sCompleted=isMine?myCompleted:cmpCompleted;
  const sOverdue=isMine?myOverdue:cmpOverdue;
  const sBlocked=isMine?myBlocked:cmpBlocked;
  const sCompRate=isMine?myCompRate:cmpCompRate;
  const sOverdueRate=isMine?myOverdueRate:cmpOverdueRate;
  const sSta=isMine?mySta:cmpSta;
  const sPri=isMine?myPri:cmpPri;
  const staTotal=Object.values(sSta).reduce((a,b)=>a+b,0);
  const priTotal=Object.values(sPri).reduce((a,b)=>a+b,0);

  // open: past due, today, tomorrow, approval all start expanded; assignedToMe and assignedByMe collapsed
  const[open,setOpen]=useState({pastDue:true,dueToday:true,dueTomorrow:true,approval:true,assignedToMe:false,assignedByMe:false});
  const toggle=k=>setOpen(p=>({...p,[k]:!p[k]}));

  const nameOf=e=>e.split("@")[0].replace(/[._]/g," ");

  const SectionHeader=({id,label,count,icon,color,defaultBg,activeBg,countBg})=>(
    <button onClick={()=>toggle(id)} className={"w-full flex items-center justify-between px-1 py-1 rounded-lg hover:bg-slate-50 mb-2"}>
      <span className="text-xs font-bold uppercase tracking-widest flex items-center gap-1.5" style={{color:color||"#64748B"}}>
        {icon}
        {label}
        {count>0&&<span className="px-1.5 py-0.5 rounded-full text-white text-xs font-bold leading-none" style={{background:countBg||color||"#64748B",fontSize:"10px"}}>{count}</span>}
      </span>
      {open[id]?<ChevronUp size={13} className="text-slate-400"/>:<ChevronDown size={13} className="text-slate-400"/>}
    </button>
  );

  const TaskLine=({t,sub,showAccept})=>(
    <div className={"flex items-center gap-2 px-4 py-3 hover:bg-slate-50 cursor-pointer"+(t.acc==="declined"?" bg-red-50":t.acc==="pending"?" bg-amber-50":"")} onClick={()=>setSel(t.id)}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{t.title}</p>
        <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
        <PriBadge pri={t.pri}/><StaBadge sta={t.sta}/>{t.acc&&<AccBadge acc={t.acc}/>}
        {showAccept&&t.acc==="pending"&&onAccept&&onDecline&&(<>
          <button onClick={e=>{e.stopPropagation();onAccept(t.id);}} className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200 font-medium">Accept</button>
          <button onClick={e=>{e.stopPropagation();onDecline(t.id);}} className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 font-medium">Decline</button>
        </>)}
      </div>
    </div>
  );

  return(
    <div className="space-y-5 max-w-2xl">
      {mode==="demo"&&(
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-start gap-3">
          <AlertCircle size={15} className="text-amber-500 shrink-0 mt-0.5"/>
          <div><p className="text-sm font-semibold text-amber-800">Demo Mode — data is not saved</p><p className="text-xs text-amber-700 mt-0.5">Sign in with Microsoft 365 to enable live SharePoint sync.</p></div>
        </div>
      )}

      {/* Stats Panel */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Toggle */}
        <div className="flex border-b border-slate-200">
          {[["mine","My Tasks"],["company","Company"]].map(([v,l])=>(
            <button key={v} onClick={()=>setStatsView(v)} className={"flex-1 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors "+(statsView===v?"border-b-2":"text-slate-400 hover:text-slate-600")} style={statsView===v?{color:BLUE,borderColor:BLUE}:{}}>
              {l}
            </button>
          ))}
        </div>
        <div className="p-4 space-y-4">
          {/* Big metric cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl p-3.5" style={{background:"#EEF4FF"}}>
              <p className="text-2xl font-bold" style={{color:BLUE}}>{sActive.length}</p>
              <p className="text-xs font-semibold mt-0.5" style={{color:BLUE}}>Active</p>
            </div>
            <div className="rounded-xl p-3.5 bg-emerald-50">
              <p className="text-2xl font-bold text-emerald-600">{sCompleted.length}</p>
              <p className="text-xs font-semibold mt-0.5 text-emerald-600">Completed</p>
            </div>
            <div className="rounded-xl p-3.5 bg-red-50">
              <p className="text-2xl font-bold text-red-600">{sOverdue.length}</p>
              <p className="text-xs font-semibold mt-0.5 text-red-600">Overdue</p>
            </div>
            <div className="rounded-xl p-3.5 bg-amber-50">
              {isMine
                ?<><p className="text-2xl font-bold text-amber-600">{sBlocked.length}</p><p className="text-xs font-semibold mt-0.5 text-amber-600">Blocked</p></>
                :<><p className="text-2xl font-bold text-amber-600">{cmpPeople.size}</p><p className="text-xs font-semibold mt-0.5 text-amber-600">People w/ Tasks</p></>
              }
            </div>
          </div>

          {/* Rate cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-100 p-3.5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{background:"#ECFDF5"}}>
                <Target size={18} className="text-emerald-600"/>
              </div>
              <div>
                <p className="text-xl font-bold text-slate-800">{sCompRate}%</p>
                <p className="text-xs text-slate-400 font-medium">Completion Rate</p>
              </div>
              <div className="ml-auto text-xs font-semibold" style={{color:sCompRate>=70?"#059669":sCompRate>=40?"#D97706":"#DC2626"}}>
                {sCompRate>=70?"Good":sCompRate>=40?"Fair":"Low"}
              </div>
            </div>
            <div className="rounded-xl border border-slate-100 p-3.5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-red-50">
                <Activity size={18} className="text-red-500"/>
              </div>
              <div>
                <p className="text-xl font-bold text-slate-800">{sOverdueRate}%</p>
                <p className="text-xs text-slate-400 font-medium">Overdue Rate</p>
              </div>
              <div className="ml-auto text-xs font-semibold" style={{color:sOverdueRate===0?"#059669":sOverdueRate<=20?"#D97706":"#DC2626"}}>
                {sOverdueRate===0?"On Track":sOverdueRate<=20?"Watch":"At Risk"}
              </div>
            </div>
          </div>

          {/* Status breakdown bar */}
          {staTotal>0&&(
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Status Breakdown</p>
              <div className="flex rounded-full overflow-hidden h-3 gap-px">
                {[
                  {key:"todo",color:"#94A3B8",label:"To Do"},
                  {key:"in_progress",color:BLUE,label:"In Progress"},
                  {key:"blocked",color:"#EF4444",label:"Blocked"},
                  {key:"review",color:"#A855F7",label:"Review"},
                  {key:"completed",color:"#10B981",label:"Completed"},
                ].filter(s=>sSta[s.key]>0).map(s=>(
                  <div key={s.key} style={{width:(sSta[s.key]/staTotal*100)+"%",background:s.color}} title={s.label+": "+sSta[s.key]}/>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                {[
                  {key:"todo",color:"#94A3B8",label:"To Do"},
                  {key:"in_progress",color:BLUE,label:"In Progress"},
                  {key:"blocked",color:"#EF4444",label:"Blocked"},
                  {key:"review",color:"#A855F7",label:"Needs Approval"},
                  {key:"completed",color:"#10B981",label:"Completed"},
                ].filter(s=>sSta[s.key]>0).map(s=>(
                  <span key={s.key} className="flex items-center gap-1 text-xs text-slate-500">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{background:s.color}}/>
                    {s.label} <strong className="text-slate-700">{sSta[s.key]}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Priority breakdown */}
          {priTotal>0&&(
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Priority of Active Tasks</p>
              <div className="grid grid-cols-3 gap-2">
                {[{k:"high",label:"High",bg:"#FEF2F2",fg:"#DC2626"},{k:"medium",label:"Medium",bg:"#FFF9EB",fg:"#D97706"},{k:"low",label:"Low",bg:"#F0FDF4",fg:"#16A34A"}].map(({k,label,bg,fg})=>(
                  <div key={k} className="rounded-lg p-2.5 text-center" style={{background:bg}}>
                    <p className="text-xl font-bold" style={{color:fg}}>{sPri[k]}</p>
                    <p className="text-xs font-semibold mt-0.5" style={{color:fg}}>{label}</p>
                    <p className="text-xs mt-0.5" style={{color:fg,opacity:0.7}}>{priTotal>0?Math.round(sPri[k]/priTotal*100):0}%</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Due date summary — my tasks only */}
          {isMine&&(
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">Coming Up</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-lg p-2.5 text-center bg-red-50">
                  <p className="text-xl font-bold text-red-600">{pastDue.length}</p>
                  <p className="text-xs font-semibold text-red-500 mt-0.5">Past Due</p>
                </div>
                <div className="rounded-lg p-2.5 text-center bg-amber-50">
                  <p className="text-xl font-bold text-amber-600">{dueToday.length}</p>
                  <p className="text-xs font-semibold text-amber-500 mt-0.5">Due Today</p>
                </div>
                <div className="rounded-lg p-2.5 text-center bg-blue-50">
                  <p className="text-xl font-bold text-blue-600">{myDueThisWeek.length}</p>
                  <p className="text-xs font-semibold text-blue-500 mt-0.5">Next 7 Days</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Needs Approval — always expanded, no collapse (action required) */}
      {needsApproval.length>0&&(
        <section>
          <SectionHeader id="approval" label="Needs Your Approval" count={needsApproval.length} icon={<ThumbsUp size={11}/>} color="#9333EA" countBg="#9333EA"/>
          {open.approval&&(
            <div className="bg-white border border-purple-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {needsApproval.map(t=>(
                <div key={t.id} className="flex items-center gap-2 px-4 py-3 bg-purple-50 hover:bg-purple-100 cursor-pointer" onClick={()=>setSel(t.id)}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{nameOf(t.asgn)} marked complete{t.dueDate?" · Due "+fmtDate(t.dueDate):""}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <StaBadge sta={t.sta}/>
                    {onApprove&&onReopen&&(<>
                      <button onClick={e=>{e.stopPropagation();onApprove(t.id);}} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-medium">Approve</button>
                      <button onClick={e=>{e.stopPropagation();onReopen(t.id);}} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 font-medium">Reopen</button>
                    </>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Past Due */}
      {pastDue.length>0&&(
        <section>
          <SectionHeader id="pastDue" label="Past Due" count={pastDue.length} icon={<AlertCircle size={11}/>} color="#DC2626" countBg="#DC2626"/>
          {open.pastDue&&(
            <div className="bg-white border border-red-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {pastDue.map(t=><TaskLine key={t.id} t={t} sub={"Due "+fmtDate(t.dueDate)+(t.asgn&&t.asgn!==email?" · "+nameOf(t.asgn):"")} showAccept={false}/>)}
            </div>
          )}
        </section>
      )}

      {/* Due Today */}
      {dueToday.length>0&&(
        <section>
          <SectionHeader id="dueToday" label="Due Today" count={dueToday.length} icon={<Clock size={11}/>} color="#D97706" countBg="#D97706"/>
          {open.dueToday&&(
            <div className="bg-white border border-amber-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {dueToday.map(t=><TaskLine key={t.id} t={t} sub={t.asgn&&t.asgn!==email?"→ "+nameOf(t.asgn):t.own!==email?"from "+nameOf(t.own):"My task"} showAccept={false}/>)}
            </div>
          )}
        </section>
      )}

      {/* Due Tomorrow */}
      {dueTomorrow.length>0&&(
        <section>
          <SectionHeader id="dueTomorrow" label="Due Tomorrow" count={dueTomorrow.length} icon={<Calendar size={11}/>} color="#2563EB" countBg="#2563EB"/>
          {open.dueTomorrow&&(
            <div className="bg-white border border-blue-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {dueTomorrow.map(t=><TaskLine key={t.id} t={t} sub={t.asgn&&t.asgn!==email?"→ "+nameOf(t.asgn):t.own!==email?"from "+nameOf(t.own):"My task"} showAccept={false}/>)}
            </div>
          )}
        </section>
      )}

      {/* Assigned to Me — collapsed by default */}
      {assignedToMe.length>0&&(
        <section>
          <SectionHeader id="assignedToMe" label="Assigned to Me" count={assignedToMe.length} icon={<UserCheck size={11}/>} color={BLUE} countBg={BLUE}/>
          {open.assignedToMe&&(
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {assignedToMe.map(t=><TaskLine key={t.id} t={t} sub={"from "+nameOf(t.own)+(t.dueDate?" · Due "+fmtDate(t.dueDate):"")} showAccept={true}/>)}
            </div>
          )}
        </section>
      )}

      {/* Tasks I've Assigned — collapsed by default */}
      {assignedByMe.length>0&&(
        <section>
          <SectionHeader id="assignedByMe" label="Tasks I've Assigned" count={assignedByMe.length} icon={<Users size={11}/>} color={GOLD} countBg={GOLD}/>
          {open.assignedByMe&&(
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
              {assignedByMe.map(t=><TaskLine key={t.id} t={t} sub={"→ "+nameOf(t.asgn)+(t.dueDate?" · Due "+fmtDate(t.dueDate):"")} showAccept={false}/>)}
            </div>
          )}
        </section>
      )}

    </div>
  );
}

//  My Day
export function MyDay({myTasks,all,sel,setSel,cu,canStandup,onStandupToggle,queue=[],setQueue,projects=[]}){
  const[open,setOpen]=useState({personal:true,company:true,assigned:true,assignedToMe:true});
  const toggle=k=>setOpen(o=>({...o,[k]:!o[k]}));
  const email=cu?.email?.toLowerCase()||"";
  const active=myTasks.filter(t=>!["completed","declined"].includes(t.sta));

  const personal=active.filter(t=>t.vis==="personal"&&t.own.toLowerCase()===email);
  const company=active.filter(t=>t.vis==="company"||t.listType==="company");
  const assigned=active.filter(t=>t.own.toLowerCase()===email&&t.asgn&&t.asgn.toLowerCase()!==email);
  const assignedToMe=active.filter(t=>t.asgn.toLowerCase()===email&&t.own.toLowerCase()!==email);
  const done=myTasks.filter(t=>t.sta==="completed"&&withinCompletedWindow(t));

  const SectionHeader=({label,count,k,color})=>(
    <button onClick={()=>toggle(k)} className="w-full flex items-center justify-between px-1 mb-2 group">
      <span className="text-xs font-bold uppercase tracking-widest flex items-center gap-1.5" style={{color:color||"#94A3B8"}}>
        {label}
        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{background:(color||"#94A3B8")+"22",color:color||"#94A3B8"}}>{count}</span>
      </span>
      {open[k]?<ChevronUp size={13} className="text-slate-400"/>:<ChevronDown size={13} className="text-slate-400"/>}
    </button>
  );

  const sections=[
    {key:"personal",label:"My Personal Tasks",tasks:personal,color:"#64748B"},
    {key:"company",label:"Company Tasks",tasks:company,color:BLUE},
    {key:"assigned",label:"Tasks I've Assigned",tasks:assigned,color:"#7C3AED"},
    {key:"assignedToMe",label:"Tasks Assigned to Me",tasks:assignedToMe,color:"#059669"},
  ];

  return(
    <div className="max-w-xl space-y-5">
      {sections.map(({key,label,tasks,color})=>(
        tasks.length>0&&(
          <div key={key}>
            <SectionHeader label={label} count={tasks.length} k={key} color={color}/>
            {open[key]&&(
              <div className="space-y-2">
                {tasks.map(t=>(
                  <TaskRow key={t.id} task={t} all={all} selected={sel===t.id} onSelect={()=>setSel(sel===t.id?null:t.id)} cu={cu} canStandup={canStandup} onStandupToggle={onStandupToggle} projects={projects}/>
                ))}
              </div>
            )}
          </div>
        )
      ))}
      {active.length===0&&<p className="text-sm text-slate-400 text-center py-12">No active tasks.</p>}
      {done.length>0&&(
        <details className="mt-2">
          <summary className="text-xs text-slate-400 cursor-pointer px-1 select-none">Show {done.length} completed</summary>
          <div className="mt-2 space-y-2">{done.map(t=><TaskRow key={t.id} task={t} all={all} selected={sel===t.id} onSelect={()=>setSel(sel===t.id?null:t.id)} cu={cu} canStandup={canStandup} onStandupToggle={onStandupToggle} projects={projects}/>)}</div>
        </details>
      )}
    </div>
  );
}

//  Work Queue
// Shared calendar view used by WorkQueue and CompanyTasks
const MONTH_NAMES=["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_NAMES=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const PRI_DOT={high:"#EF4444",medium:"#F59E0B",low:"#6EE7B7"};
function CalendarView({tasks,sel,setSel}){
  const today=new Date().toISOString().slice(0,10);
  const[curMonth,setCurMonth]=useState(()=>{const d=new Date();return{y:d.getFullYear(),m:d.getMonth()};});
  const prevMonth=()=>setCurMonth(({y,m})=>m===0?{y:y-1,m:11}:{y,m:m-1});
  const nextMonth=()=>setCurMonth(({y,m})=>m===11?{y:y+1,m:0}:{y,m:m+1});
  const firstDow=new Date(curMonth.y,curMonth.m,1).getDay();
  const daysInMonth=new Date(curMonth.y,curMonth.m+1,0).getDate();
  const tasksByDate={};
  const noDate=[];
  tasks.forEach(t=>{
    if(t.dueDate){if(!tasksByDate[t.dueDate])tasksByDate[t.dueDate]=[];tasksByDate[t.dueDate].push(t);}
    else noDate.push(t);
  });
  const cells=[];
  for(let i=0;i<firstDow;i++)cells.push(null);
  for(let d=1;d<=daysInMonth;d++){
    const mm=String(curMonth.m+1).padStart(2,"0");
    const dd=String(d).padStart(2,"0");
    cells.push(`${curMonth.y}-${mm}-${dd}`);
  }
  return(
    <div>
      <div className="flex items-center justify-between mb-3 bg-white border border-slate-200 rounded-xl px-4 py-2">
        <button onClick={prevMonth} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><ChevronLeft size={16}/></button>
        <span className="text-sm font-semibold text-slate-700">{MONTH_NAMES[curMonth.m]} {curMonth.y}</span>
        <button onClick={nextMonth} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><ChevronRight size={16}/></button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_NAMES.map(d=><div key={d} className="text-xs font-semibold text-slate-400 text-center py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((date,i)=>{
          if(!date)return<div key={`e${i}`} className="min-h-[80px] rounded-lg bg-slate-50/50"/>;
          const dayTasks=tasksByDate[date]||[];
          const isToday=date===today;
          const isPast=date<today;
          const dayNum=parseInt(date.slice(8));
          return(
            <div key={date} className={"min-h-[80px] rounded-lg p-1.5 border "+(isToday?"border-blue-400 bg-blue-50":isPast&&dayTasks.length>0?"border-red-100 bg-red-50/40":"border-slate-100 bg-white")}>
              <div className={"text-xs font-bold mb-1 w-5 h-5 flex items-center justify-center rounded-full "+(isToday?"bg-blue-500 text-white":isPast?"text-slate-400":"text-slate-600")}>{dayNum}</div>
              <div className="space-y-0.5">
                {dayTasks.slice(0,3).map(t=>(
                  <div key={t.id} onClick={()=>setSel(sel===t.id?null:t.id)} className={"text-xs truncate px-1.5 py-0.5 rounded cursor-pointer flex items-center gap-1 "+(sel===t.id?"text-white":isOverdue(t)?"bg-red-100 text-red-700 hover:bg-red-200":"bg-slate-100 text-slate-700 hover:bg-slate-200")} style={sel===t.id?{background:BLUE}:{}}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:PRI_DOT[t.pri]||"#94A3B8"}}/>
                    <span className="truncate">{t.title}</span>
                  </div>
                ))}
                {dayTasks.length>3&&<div className="text-xs text-slate-400 px-1">+{dayTasks.length-3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
      {noDate.length>0&&(
        <div className="mt-3 bg-white border border-slate-200 rounded-xl p-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1"><Clock size={10}/>No Due Date ({noDate.length})</p>
          <div className="space-y-1">
            {noDate.map(t=>(
              <div key={t.id} onClick={()=>setSel(sel===t.id?null:t.id)} className={"text-xs px-2 py-1 rounded-lg cursor-pointer flex items-center gap-1.5 "+(sel===t.id?"text-white":"bg-slate-50 text-slate-600 hover:bg-slate-100")} style={sel===t.id?{background:BLUE}:{}}>
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{background:PRI_DOT[t.pri]||"#94A3B8"}}/>
                {t.title}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function WorkQueue({tasks,all,queue,setQueue,sel,setSel,onAccept,onDecline,cu,canStandup,onStandupToggle,projects=[]}){
  const email=cu?.email?.toLowerCase()||"";
  const[showCompleted,setShowCompleted]=useState(false);
  const[filters,setFilters]=useState({personal:true,company:true,assigned:true,companyAssigned:true});
  const[dragId,setDragId]=useState(null);
  const[dragOverId,setDragOverId]=useState(null);
  const[viewMode,setViewMode]=useState("list");

  const toggleFilter=k=>setFilters(f=>({...f,[k]:!f[k]}));

  // Categorize each task
  const getCategory=t=>{
    if(t.vis==="personal") return "personal";
    if((t.vis==="company"||t.listType==="company")&&t.asgn&&t.asgn.toLowerCase()===email) return "companyAssigned";
    if(t.vis==="company"||t.listType==="company") return "company";
    if(t.vis==="assigned") return "assigned";
    return "personal";
  };

  // All active tasks visible to me in queue order
  // Exclude tasks I own but assigned to someone else — those belong in the assignee's queue
  const allActive=queue.map(id=>tasks.find(t=>t.id===id)).filter(Boolean).filter(t=>
    !["completed","declined"].includes(t.sta)&&
    !(t.own.toLowerCase()===email&&t.asgn&&t.asgn.toLowerCase()!==email)
  );

  // Apply category filters
  const visible=allActive.filter(t=>filters[getCategory(t)]);

  // Pending acceptance tasks (always shown separately)
  const pending=tasks.filter(t=>t.asgn.toLowerCase()===email&&t.acc==="pending");

  // Completed tasks — last 15 days only
  const completedTasks=tasks.filter(t=>
    (t.own.toLowerCase()===email||t.asgn.toLowerCase()===email)&&t.sta==="completed"&&withinCompletedWindow(t)
  ).sort((a,b)=>(b.completedDate||b.dueDate||"")>(a.completedDate||a.dueDate||"")?1:-1);

  // Drag handlers
  const onDragStart=(e,id)=>{
    setDragId(id);
    e.dataTransfer.effectAllowed="move";
  };
  const onDragOver=(e,id)=>{
    e.preventDefault();
    e.dataTransfer.dropEffect="move";
    if(id!==dragId) setDragOverId(id);
  };
  const onDrop=(e,targetId)=>{
    e.preventDefault();
    if(!dragId||dragId===targetId){setDragId(null);setDragOverId(null);return;}
    const next=[...queue];
    const fromIdx=next.indexOf(dragId);
    const toIdx=next.indexOf(targetId);
    if(fromIdx<0||toIdx<0){setDragId(null);setDragOverId(null);return;}
    next.splice(fromIdx,1);
    next.splice(toIdx,0,dragId);
    setQueue(next);
    setDragId(null);setDragOverId(null);
  };
  const onDragEnd=()=>{setDragId(null);setDragOverId(null);};

  const CAT_META={
    personal:{label:"Personal",color:"#64748B",bg:"#F1F5F9"},
    company:{label:"Company",color:BLUE,bg:BLUE+"15"},
    assigned:{label:"Assigned",color:"#059669",bg:"#ECFDF5"},
    companyAssigned:{label:"Company Assigned",color:"#7C3AED",bg:"#F5F3FF"},
  };

  const FilterBtn=({k})=>{
    const m=CAT_META[k];
    return(
      <button onClick={()=>toggleFilter(k)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all" style={filters[k]?{background:m.color,color:"white",borderColor:m.color}:{background:"white",color:"#94A3B8",borderColor:"#E2E8F0"}}>
        {m.label}
      </button>
    );
  };

  return(
    <div className="max-w-xl space-y-4">
      {/* Filter buttons + view toggle */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Show</p>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
            <button onClick={()=>setViewMode("list")} className={"flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all "+(viewMode==="list"?"bg-white text-slate-700 shadow-sm":"text-slate-400 hover:text-slate-600")}><List size={12}/>List</button>
            <button onClick={()=>setViewMode("calendar")} className={"flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all "+(viewMode==="calendar"?"bg-white text-slate-700 shadow-sm":"text-slate-400 hover:text-slate-600")}><Calendar size={12}/>Calendar</button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <FilterBtn k="personal"/>
          <FilterBtn k="company"/>
          <FilterBtn k="assigned"/>
          <FilterBtn k="companyAssigned"/>
        </div>
      </div>

      {/* Pending acceptance */}
      {pending.length>0&&(
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-600 flex items-center gap-1 px-1"><Bell size={10}/>Pending Acceptance</p>
          {pending.map(t=>(
            <div key={t.id} className="bg-white border border-amber-300 rounded-xl px-4 py-3">
              <p className="text-sm font-semibold mb-0.5">{t.title}</p>
              <div className="flex items-center justify-between mt-2">
                <div className="flex gap-1.5"><PriBadge pri={t.pri}/><span className="text-xs text-slate-400">Due {fmtDate(t.dueDate)}</span></div>
                <div className="flex gap-2">
                  <button onClick={()=>onDecline(t.id)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><ThumbsDown size={10}/>Decline</button>
                  <button onClick={()=>onAccept(t.id)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg text-white" style={{background:BLUE}}><ThumbsUp size={10}/>Accept</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Calendar or List */}
      {viewMode==="calendar"?(
        <CalendarView tasks={visible} sel={sel} setSel={setSel}/>
      ):visible.length>0?(
        <div className="space-y-2">
          {visible.map(t=>{
            const cat=getCategory(t);
            const m=CAT_META[cat];
            const stale=needsStandupUpdate(t);
            const isDragging=dragId===t.id;
            const isOver=dragOverId===t.id;
            return(
              <div
                key={t.id}
                draggable
                onDragStart={e=>onDragStart(e,t.id)}
                onDragOver={e=>onDragOver(e,t.id)}
                onDrop={e=>onDrop(e,t.id)}
                onDragEnd={onDragEnd}
                className={"flex items-center gap-3 bg-white border rounded-xl px-3 py-3 transition-all "+(isDragging?"opacity-40 scale-95":isOver?"border-blue-400 shadow-md":(stale&&t.standup?"border-amber-300 bg-amber-50":sel===t.id?"border-blue-300":"border-slate-200"))}
                style={sel===t.id&&!isOver?{boxShadow:"0 0 0 3px "+BLUE+"22"}:{}}
              >
                {/* Drag handle */}
                <div className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 shrink-0">
                  <GripVertical size={16}/>
                </div>
                {/* Category badge */}
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0" style={{background:m.bg,color:m.color}}>{m.label}</span>
                {/* Task info */}
                <div className="flex-1 min-w-0 cursor-pointer" onClick={()=>setSel(sel===t.id?null:t.id)}>
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    {t.standup&&<span className={"text-xs px-1.5 py-0.5 rounded font-semibold flex items-center gap-1 "+(stale?"bg-amber-200 text-amber-800":"bg-blue-100 text-blue-700")}><Radio size={9}/>Standup{stale?" ⚠":""}</span>}
                  </div>
                  <div className="flex gap-1.5 flex-wrap items-center">
                    <PriBadge pri={t.pri}/>
                    <StaBadge sta={t.sta}/>
                    {isOverdue(t)&&<span className="text-xs text-red-500 font-semibold">Overdue</span>}
                    {t.dueDate&&<span className="text-xs text-slate-400">{fmtDate(t.dueDate)}</span>}
                    {t.project&&projects.length>0&&(()=>{const p=projects.find(x=>x.spId===t.project);return p?<span className="text-xs flex items-center gap-1"><Layers size={9} className="text-indigo-400 shrink-0"/>{p.customerName&&<span className="text-slate-400">{p.customerName} ·</span>}<span className="text-indigo-600 font-medium">{p.jobNumber?p.jobNumber+" – "+p.projectName:p.projectName}</span></span>:null;})()}
                  </div>
                </div>
                {/* Standup toggle */}
                {canStandup&&(
                  <button onClick={e=>{e.stopPropagation();onStandupToggle&&onStandupToggle(t,!t.standup);}} title={t.standup?"Remove from Daily Standup":"Add to Daily Standup"} className={"p-1 rounded-lg transition-colors shrink-0 "+(t.standup?"text-blue-600 bg-blue-100 hover:bg-blue-200":"text-slate-300 hover:text-blue-400 hover:bg-blue-50")}>
                    <Radio size={14}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ):(
        <p className="text-sm text-slate-400 text-center py-12">No tasks match the selected filters.</p>
      )}

      {/* Completed — list mode only */}
      {viewMode==="list"&&completedTasks.length>0&&(
        <div className="mt-2">
          <button onClick={()=>setShowCompleted(s=>!s)} className="w-full flex items-center justify-center gap-2 py-2 text-xs text-slate-400 hover:text-slate-600 border border-dashed border-slate-200 rounded-xl hover:bg-slate-50">
            {showCompleted?"Hide":"Show"} {completedTasks.length} completed task{completedTasks.length!==1?"s":""} (last {COMPLETED_DAYS} days)
          </button>
          {showCompleted&&(
            <div className="mt-2 space-y-1.5">
              {completedTasks.map(t=>(
                <div key={t.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-3 py-2.5 opacity-60 cursor-pointer hover:opacity-100" onClick={()=>setSel(sel===t.id?null:t.id)}>
                  <CheckCircle2 size={15} className="text-emerald-400 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm line-through text-slate-400 truncate">{t.title}</p>
                    {t.dueDate&&<p className="text-xs text-slate-300">Due {fmtDate(t.dueDate)}</p>}
                  </div>
                  <StaBadge sta={t.sta}/>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

//  Company Tasks
const SORT_KEYS=["user","priority","project","status"];
const SORT_LABELS={user:"User",priority:"Priority",project:"Project",status:"Status"};
function compUserDisplay(t){
  // Sort/group by assignee once accepted, owner otherwise
  return(t.asgn&&t.acc==="accepted")?t.asgn:t.own;
}
function compSortVal(t,key,projects){
  if(key==="user")return dispName(compUserDisplay(t)).toLowerCase();
  if(key==="priority")return PRI_ORDER[t.pri]??1;
  if(key==="status")return STA_ORDER[t.sta]??0;
  if(key==="project"){const p=(projects||[]).find(x=>x.spId===t.project);return p?(p.jobNumber||p.projectName).toLowerCase():"zzz";}
  return 0;
}
function compGroupLabel(t,key,projects){
  if(key==="user")return dispName(compUserDisplay(t));
  if(key==="priority")return{high:"High",medium:"Medium",low:"Low"}[t.pri]||t.pri;
  if(key==="status")return{todo:"To Do",in_progress:"In Progress",completed:"Completed",blocked:"Blocked",pending:"Pending",declined:"Declined"}[t.sta]||t.sta;
  if(key==="project"){const p=(projects||[]).find(x=>x.spId===t.project);return p?(p.jobNumber?p.jobNumber+" – "+p.projectName:p.projectName):"No Project";}
  return"";
}
const GROUP_ICONS={user:<Users size={11}/>,priority:<AlertCircle size={11}/>,project:<Layers size={11}/>,status:<ChevronRight size={11}/>};
export function CompanyTasks({tasks,sel,setSel,cu,canStandup,onStandupToggle,projects=[]}){
  const[sort1,setSort1]=useState("user");
  const[sort2,setSort2]=useState("priority");
  const[showCompleted,setShowCompleted]=useState(false);
  const[viewMode,setViewMode]=useState("list");
  const company=tasks.filter(t=>(t.vis==="company"||t.listType==="company")&&t.sta!=="completed");
  const companyDone=tasks.filter(t=>(t.vis==="company"||t.listType==="company")&&t.sta==="completed"&&withinCompletedWindow(t));
  const sorted=[...company].sort((a,b)=>{
    const av=compSortVal(a,sort1,projects),bv=compSortVal(b,sort1,projects);
    if(av<bv)return -1;if(av>bv)return 1;
    const av2=compSortVal(a,sort2,projects),bv2=compSortVal(b,sort2,projects);
    if(av2<bv2)return -1;if(av2>bv2)return 1;
    return 0;
  });
  // Build ordered groups by primary sort key
  const groupMap=new Map();
  const groupOrder=[];
  for(const t of sorted){
    const lbl=compGroupLabel(t,sort1,projects);
    if(!groupMap.has(lbl)){groupMap.set(lbl,[]);groupOrder.push(lbl);}
    groupMap.get(lbl).push(t);
  }
  const changeSort1=(s)=>{setSort1(s);if(sort2===s)setSort2(SORT_KEYS.find(k=>k!==s));};
  return(
    <div className="max-w-2xl space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-xs font-semibold text-slate-500">{company.length} task{company.length!==1?"s":""}</span>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          <button onClick={()=>setViewMode("list")} className={"flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all "+(viewMode==="list"?"bg-white text-slate-700 shadow-sm":"text-slate-400 hover:text-slate-600")}><List size={12}/>List</button>
          <button onClick={()=>setViewMode("calendar")} className={"flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all "+(viewMode==="calendar"?"bg-white text-slate-700 shadow-sm":"text-slate-400 hover:text-slate-600")}><Calendar size={12}/>Calendar</button>
        </div>
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400 font-medium">Sort by</span>
            <div className="flex gap-1 flex-wrap">
              {SORT_KEYS.map(s=>(
                <button key={s} onClick={()=>changeSort1(s)} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(sort1===s?"text-white border-transparent":"text-slate-500 border-slate-200 hover:bg-slate-50")} style={sort1===s?{background:BLUE}:{}}>{SORT_LABELS[s]}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400 font-medium">then</span>
            <div className="flex gap-1 flex-wrap">
              {SORT_KEYS.filter(s=>s!==sort1).map(s=>(
                <button key={s} onClick={()=>setSort2(s)} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(sort2===s?"text-white border-transparent":"text-slate-500 border-slate-200 hover:bg-slate-50")} style={sort2===s?{background:GOLD}:{}}>{SORT_LABELS[s]}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {viewMode==="calendar"?(
        <CalendarView tasks={company} sel={sel} setSel={setSel}/>
      ):sorted.length===0
        ?<p className="text-sm text-slate-400 text-center py-12">No company tasks.</p>
        :groupOrder.map(lbl=>(
          <div key={lbl}>
            <div className="flex items-center gap-2 px-1 mb-2 mt-1">
              <span className="text-slate-400">{GROUP_ICONS[sort1]}</span>
              <span className="text-sm font-bold text-slate-700">{lbl}</span>
              <span className="text-xs text-slate-400 font-normal">({groupMap.get(lbl).length})</span>
              <div className="flex-1 border-t border-slate-200 ml-1"/>
            </div>
            <div className="space-y-2">
              {groupMap.get(lbl).map(t=><TaskRow key={t.id} task={t} all={tasks} selected={sel===t.id} onSelect={()=>setSel(sel===t.id?null:t.id)} cu={cu} canStandup={canStandup} onStandupToggle={onStandupToggle} projects={projects}/>)}
            </div>
          </div>
        ))
      }
      {viewMode==="list"&&companyDone.length>0&&(
        <div className="mt-1">
          <button onClick={()=>setShowCompleted(s=>!s)} className="w-full flex items-center justify-center gap-2 py-2 text-xs text-slate-400 hover:text-slate-600 border border-dashed border-slate-200 rounded-xl hover:bg-slate-50">
            {showCompleted?"Hide":"Show"} {companyDone.length} completed task{companyDone.length!==1?"s":""} (last {COMPLETED_DAYS} days)
          </button>
          {showCompleted&&(
            <div className="mt-2 space-y-2">
              {companyDone.map(t=>(
                <div key={t.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3 opacity-60 cursor-pointer hover:opacity-100" onClick={()=>setSel(sel===t.id?null:t.id)}>
                  <CheckCircle2 size={15} className="text-emerald-400 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm line-through text-slate-400 truncate">{t.title}</p>
                    <p className="text-xs text-slate-300">{t.own.split("@")[0]}{t.dueDate?" · "+fmtDate(t.dueDate):""}</p>
                  </div>
                  <StaBadge sta={t.sta}/>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

//  Inbox 
export function InboxView({inboxPending,inboxNotifs,sel,setSel,onAccept,onDecline}){
  return(
    <div className="max-w-xl space-y-6">
      {inboxPending.length===0&&inboxNotifs.length===0&&(
        <div className="text-center py-16 text-slate-400"><Inbox size={32} className="mx-auto mb-3 opacity-30"/><p className="text-sm">Inbox is clear</p></div>
      )}
      {inboxPending.length>0&&(
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5 text-amber-600"><Bell size={11}/>Action Required</h3>
          <div className="space-y-3">
            {inboxPending.map(t=>(
              <div key={t.id} onClick={()=>setSel(sel===t.id?null:t.id)} className="bg-white border border-amber-300 rounded-xl px-4 py-4 cursor-pointer hover:shadow-sm" style={sel===t.id?{borderColor:BLUE,boxShadow:"0 0 0 3px "+BLUE+"22"}:{}}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium flex items-center gap-1"><UserCheck size={9}/>Assigned by {(t.reqBy||t.own).split("@")[0]}</span>
                  <PriBadge pri={t.pri}/>
                </div>
                <p className="text-sm font-semibold mb-1">{t.title}</p>
                <p className="text-xs text-slate-500 mb-3 line-clamp-2">{t.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400">Due {fmtDate(t.dueDate)}</span>
                  <div className="flex gap-2" onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>onDecline(t.id)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium"><ThumbsDown size={11}/>Decline</button>
                    <button onClick={()=>onAccept(t.id)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg text-white font-medium" style={{background:BLUE}}><ThumbsUp size={11}/>Accept</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {inboxNotifs.length>0&&(
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-1.5 text-slate-500"><Bell size={11}/>Notifications</h3>
          <div className="space-y-2">
            {inboxNotifs.map(t=>(
              <div key={t.id} onClick={()=>setSel(sel===t.id?null:t.id)} className={"bg-white border rounded-xl px-4 py-3 cursor-pointer hover:shadow-sm "+(t.acc==="declined"?"border-red-200":"border-emerald-200")} style={sel===t.id?{borderColor:BLUE,boxShadow:"0 0 0 3px "+BLUE+"22"}:{}}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={"text-xs px-2 py-0.5 rounded-full font-medium border flex items-center gap-1 "+(t.acc==="declined"?"bg-red-50 text-red-600 border-red-200":"bg-emerald-50 text-emerald-600 border-emerald-200")}>
                    {t.acc==="declined"?<ThumbsDown size={9}/>:<ThumbsUp size={9}/>}
                    {t.asgn.split("@")[0]} {t.acc} your task
                  </span>
                  <PriBadge pri={t.pri}/>
                </div>
                <p className="text-sm font-medium mt-1.5">{t.title}</p>
                {t.acc==="declined"&&<p className="text-xs text-slate-400 mt-1">This task needs to be reassigned or deleted.</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

//  My Team
export function TeamView({tasks,cu,allUsers,sel,setSel,canStandup,onStandupToggle,projects=[]}){
  const[sort1,setSort1]=useState("user");
  const[sort2,setSort2]=useState("priority");
  const[showCompleted,setShowCompleted]=useState(false);
  const email=cu?.email?.toLowerCase()||"";
  const myReports=allUsers.filter(u=>u.manager1?.toLowerCase()===email||u.manager2?.toLowerCase()===email);
  if(myReports.length===0){
    return(
      <div className="text-center py-16 text-slate-400">
        <Users size={32} className="mx-auto mb-3 opacity-30"/>
        <p className="text-sm">No direct reports configured</p>
        <p className="text-xs mt-1">Ask an Admin to set your reporting relationships in the Admin panel.</p>
      </div>
    );
  }
  const reportEmails=new Set(myReports.map(u=>u.email.toLowerCase()));
  // Non-personal tasks where a report is the responsible person (assignee if accepted, else owner)
  const teamTasks=tasks.filter(t=>{
    if(t.vis==="personal")return false;
    const responsible=(t.asgn&&t.acc==="accepted"?t.asgn:t.own).toLowerCase();
    return reportEmails.has(responsible);
  });
  const active=teamTasks.filter(t=>!["completed","declined"].includes(t.sta));
  const done=teamTasks.filter(t=>t.sta==="completed"&&withinCompletedWindow(t));
  const sorted=[...active].sort((a,b)=>{
    const av=compSortVal(a,sort1,projects),bv=compSortVal(b,sort1,projects);
    if(av<bv)return -1;if(av>bv)return 1;
    const av2=compSortVal(a,sort2,projects),bv2=compSortVal(b,sort2,projects);
    if(av2<bv2)return -1;if(av2>bv2)return 1;
    return 0;
  });
  const groupMap=new Map();
  const groupOrder=[];
  for(const t of sorted){
    const lbl=compGroupLabel(t,sort1,projects);
    if(!groupMap.has(lbl)){groupMap.set(lbl,[]);groupOrder.push(lbl);}
    groupMap.get(lbl).push(t);
  }
  const changeSort1=(s)=>{setSort1(s);if(sort2===s)setSort2(SORT_KEYS.find(k=>k!==s));};
  return(
    <div className="max-w-2xl space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-xs font-semibold text-slate-500">{active.length} task{active.length!==1?"s":""}</span>
        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400 font-medium">Sort by</span>
            <div className="flex gap-1 flex-wrap">
              {SORT_KEYS.map(s=>(
                <button key={s} onClick={()=>changeSort1(s)} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(sort1===s?"text-white border-transparent":"text-slate-500 border-slate-200 hover:bg-slate-50")} style={sort1===s?{background:BLUE}:{}}>{SORT_LABELS[s]}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400 font-medium">then</span>
            <div className="flex gap-1 flex-wrap">
              {SORT_KEYS.filter(s=>s!==sort1).map(s=>(
                <button key={s} onClick={()=>setSort2(s)} className={"text-xs px-2.5 py-1 rounded-full border font-medium "+(sort2===s?"text-white border-transparent":"text-slate-500 border-slate-200 hover:bg-slate-50")} style={sort2===s?{background:GOLD}:{}}>{SORT_LABELS[s]}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {sorted.length===0
        ?<p className="text-sm text-slate-400 text-center py-12">No team tasks.</p>
        :groupOrder.map(lbl=>(
          <div key={lbl}>
            <div className="flex items-center gap-2 px-1 mb-2 mt-1">
              <span className="text-slate-400">{GROUP_ICONS[sort1]}</span>
              <span className="text-sm font-bold text-slate-700">{lbl}</span>
              <span className="text-xs text-slate-400 font-normal">({groupMap.get(lbl).length})</span>
              <div className="flex-1 border-t border-slate-200 ml-1"/>
            </div>
            <div className="space-y-2">
              {groupMap.get(lbl).map(t=><TaskRow key={t.id} task={t} all={tasks} selected={sel===t.id} onSelect={()=>setSel&&setSel(sel===t.id?null:t.id)} cu={cu} canStandup={canStandup} onStandupToggle={onStandupToggle} projects={projects}/>)}
            </div>
          </div>
        ))
      }
      {done.length>0&&(
        <div className="mt-1">
          <button onClick={()=>setShowCompleted(s=>!s)} className="w-full flex items-center justify-center gap-2 py-2 text-xs text-slate-400 hover:text-slate-600 border border-dashed border-slate-200 rounded-xl hover:bg-slate-50">
            {showCompleted?"Hide":"Show"} {done.length} completed task{done.length!==1?"s":""} (last {COMPLETED_DAYS} days)
          </button>
          {showCompleted&&(
            <div className="mt-2 space-y-2">
              {done.map(t=>(
                <div key={t.id} className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-4 py-3 opacity-60 cursor-pointer hover:opacity-100" onClick={()=>setSel&&setSel(sel===t.id?null:t.id)}>
                  <CheckCircle2 size={15} className="text-emerald-400 shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm line-through text-slate-400 truncate">{t.title}</p>
                    <p className="text-xs text-slate-300">{dispName(t.own).split(" ")[0]}{t.dueDate?" · "+fmtDate(t.dueDate):""}</p>
                  </div>
                  <StaBadge sta={t.sta}/>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
